import { useEffect, useState } from "react";
import { useNotify, useRedirect } from "ra-core";
import { ResponsiveBar } from "@nivo/bar";
import {
  AlertCircle, CheckCircle, Clock, DollarSign, RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";

type ARRec = {
  id: number;
  order_id: number;
  order_number: string;
  contact_name: string;
  company_name: string;
  type: "deposit" | "balance";
  amount: number;
  currency: string;
  due_date: string;
  status: string;
  days_until_due: number;
  aging_bucket: string;
};

const BUCKETS = [
  { key: "future", label: "未到期", color: "bg-blue-50 text-blue-700 border-blue-200", bar: "#3b82f6" },
  { key: "0-30", label: "0–30 天", color: "bg-green-50 text-green-700 border-green-200", bar: "#22c55e" },
  { key: "31-60", label: "31–60 天", color: "bg-yellow-50 text-yellow-700 border-yellow-200", bar: "#eab308" },
  { key: "61-90", label: "61–90 天", color: "bg-orange-50 text-orange-700 border-orange-200", bar: "#f97316" },
  { key: "90+", label: "90+ 天", color: "bg-red-50 text-red-700 border-red-200", bar: "#ef4444" },
];

const fmtAmt = (n: number, c = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n ?? 0);

const getAgingBucket = (days: number) => {
  if (days > 0) return "future";
  const d = Math.abs(days);
  if (d <= 30) return "0-30";
  if (d <= 60) return "31-60";
  if (d <= 90) return "61-90";
  return "90+";
};

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-blue-100 text-blue-700",
  overdue: "bg-red-100 text-red-700",
  cleared: "bg-green-100 text-green-700",
  written_off: "bg-gray-100 text-gray-500",
};

const ARPage = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [records, setRecords] = useState<ARRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [activeBucket, setActiveBucket] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"active" | "cleared">("active");

  const load = async () => {
    setLoading(true);
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from("ar_records")
      .select(`
        id, order_id, type, amount, currency, due_date, status,
        orders ( order_number, contacts ( first_name, last_name ), companies ( name ) )
      `)
      .in("status", statusFilter === "active" ? ["pending", "overdue"] : ["cleared", "written_off"])
      .order("due_date", { ascending: true });

    const now = new Date();
    const rows: ARRec[] = (data ?? []).map((r: any) => {
      const order = r.orders ?? {};
      const contact = order.contacts ?? {};
      const due = new Date(r.due_date);
      const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return {
        id: r.id,
        order_id: r.order_id,
        order_number: order.order_number ?? `ORD-${String(r.order_id).padStart(4, "0")}`,
        contact_name: contact.first_name ? `${contact.first_name} ${contact.last_name}` : "—",
        company_name: order.companies?.name ?? "—",
        type: r.type,
        amount: r.amount ?? 0,
        currency: r.currency,
        due_date: r.due_date,
        status: r.status,
        days_until_due: daysUntilDue,
        aging_bucket: getAgingBucket(daysUntilDue),
      };
    });
    setRecords(rows);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleMarkOverdue = async () => {
    setChecking(true);
    const { data, error } = await getSupabaseClient().rpc("mark_overdue_ar_records");
    if (error) {
      notify("操作失败: " + error.message, { type: "error" });
    } else {
      notify(`已标记 ${data ?? 0} 条应收账款为逾期`, { type: "success" });
      load();
    }
    setChecking(false);
  };

  const activeRecords = records.filter(r => statusFilter === "active");
  const totalUSD = activeRecords.filter(r => r.currency === "USD").reduce((s, r) => s + r.amount, 0);
  const totalEUR = activeRecords.filter(r => r.currency === "EUR").reduce((s, r) => s + r.amount, 0);
  const overdueCount = activeRecords.filter(r => r.status === "overdue").length;

  const filtered = activeBucket === "all"
    ? records
    : records.filter(r => r.aging_bucket === activeBucket);

  // Chart data
  const chartData = BUCKETS.map(b => ({
    bucket: b.label,
    USD: Math.round(records.filter(r => r.aging_bucket === b.key && r.currency === "USD").reduce((s, r) => s + r.amount, 0)),
    EUR: Math.round(records.filter(r => r.aging_bucket === b.key && r.currency === "EUR").reduce((s, r) => s + r.amount, 0)),
    count: records.filter(r => r.aging_bucket === b.key).length,
  })).filter(d => d.count > 0);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6" /> 应收账款
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">AR 管理 · 账龄分析 · 逾期预警</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            {(["active", "cleared"] as const).map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setActiveBucket("all"); }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {s === "active" ? "待收款" : "已清账"}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" disabled={checking} onClick={handleMarkOverdue}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${checking ? "animate-spin" : ""}`} />
            {checking ? "检查中..." : "检查逾期"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : (
        <>
          {/* KPI cards */}
          {statusFilter === "active" && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">待收 (USD)</div>
                <div className="text-xl font-bold tabular-nums text-blue-600">{fmtAmt(totalUSD, "USD")}</div>
                <div className="text-xs text-muted-foreground">{records.filter(r => r.currency === "USD").length} 条</div>
              </div>
              {totalEUR > 0 && (
                <div className="bg-card border rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-1">待收 (EUR)</div>
                  <div className="text-xl font-bold tabular-nums text-purple-600">{fmtAmt(totalEUR, "EUR")}</div>
                  <div className="text-xs text-muted-foreground">{records.filter(r => r.currency === "EUR").length} 条</div>
                </div>
              )}
              <div className="bg-card border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">逾期笔数</div>
                <div className={`text-xl font-bold ${overdueCount > 0 ? "text-destructive" : "text-green-600"}`}>
                  {overdueCount}
                </div>
                <div className="text-xs text-muted-foreground">需跟进</div>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">AR 总条数</div>
                <div className="text-xl font-bold">{records.length}</div>
                <div className="text-xs text-muted-foreground">待收款</div>
              </div>
            </div>
          )}

          {/* Aging chart */}
          {chartData.length > 0 && statusFilter === "active" && (
            <div className="bg-card border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-4">账龄分布（金额）</h2>
              <div className="h-44">
                <ResponsiveBar
                  data={chartData}
                  keys={["USD", "EUR"]}
                  indexBy="bucket"
                  groupMode="grouped"
                  layout="vertical"
                  margin={{ top: 5, right: 20, bottom: 40, left: 70 }}
                  padding={0.3}
                  colors={["#3b82f6", "#a855f7"]}
                  borderRadius={3}
                  axisBottom={{ tickSize: 0, tickPadding: 8 }}
                  axisLeft={{ tickSize: 0, tickPadding: 8, format: (v: number) => v >= 1000 ? `$${v/1000}k` : `$${v}` }}
                  enableLabel={false}
                  enableGridX={false}
                  theme={{ axis: { ticks: { text: { fontSize: 11, fill: "#888" } } } }}
                  legends={[{ dataFrom: "keys", anchor: "bottom-right", direction: "row", translateY: 38, itemWidth: 60, itemHeight: 16, symbolSize: 10, symbolShape: "square" }]}
                />
              </div>
            </div>
          )}

          {/* Aging buckets (clickable filter) */}
          {statusFilter === "active" && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {BUCKETS.map(b => {
                const count = records.filter(r => r.aging_bucket === b.key).length;
                const amt = records.filter(r => r.aging_bucket === b.key && r.currency === "USD").reduce((s, r) => s + r.amount, 0);
                return (
                  <button key={b.key} onClick={() => setActiveBucket(activeBucket === b.key ? "all" : b.key)}
                    className={`border rounded-lg p-3 text-left transition-all ${b.color} ${activeBucket === b.key ? "ring-2 ring-offset-1 ring-current" : ""}`}>
                    <div className="text-xs font-medium mb-1">{b.label}</div>
                    <div className="text-lg font-bold">{count} 笔</div>
                    {amt > 0 && <div className="text-xs mt-0.5">${Math.round(amt).toLocaleString()}</div>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div className="bg-card border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                {activeBucket === "all" ? `全部 (${filtered.length})` : `${BUCKETS.find(b => b.key === activeBucket)?.label} (${filtered.length})`}
              </h2>
              {activeBucket !== "all" && (
                <button onClick={() => setActiveBucket("all")} className="text-xs text-muted-foreground hover:text-foreground">
                  清除筛选 ×
                </button>
              )}
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center py-16 text-muted-foreground gap-2">
                <CheckCircle className="w-10 h-10 text-green-400" />
                <p className="font-medium">此分类无待收账款</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                      <th className="text-left px-5 py-2.5">订单</th>
                      <th className="text-left px-4 py-2.5">客户 / 公司</th>
                      <th className="text-center px-4 py-2.5">类型</th>
                      <th className="text-right px-4 py-2.5">金额</th>
                      <th className="text-center px-4 py-2.5 hidden md:table-cell">到期日</th>
                      <th className="text-center px-5 py-2.5">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr key={r.id} className="border-t hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => redirect("show", "orders", r.order_id)}>
                        <td className="px-5 py-3">
                          <div className="font-medium">{r.order_number}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div>{r.contact_name}</div>
                          <div className="text-xs text-muted-foreground">{r.company_name}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                            {r.type === "deposit" ? "定金" : "尾款"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">
                          {fmtAmt(r.amount, r.currency)}
                        </td>
                        <td className="px-4 py-3 text-center hidden md:table-cell text-xs">
                          <div>{r.due_date}</div>
                          {r.days_until_due < 0 && (
                            <div className="flex items-center justify-center gap-0.5 text-destructive">
                              <AlertCircle className="w-3 h-3" />
                              逾期 {Math.abs(r.days_until_due)}d
                            </div>
                          )}
                          {r.days_until_due >= 0 && r.days_until_due <= 7 && (
                            <div className="flex items-center justify-center gap-0.5 text-orange-500">
                              <Clock className="w-3 h-3" />
                              {r.days_until_due}d 到期
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] ?? "bg-muted"}`}>
                            {r.status === "pending" ? "待收" : r.status === "overdue" ? "逾期" : r.status === "cleared" ? "已清" : "核销"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ARPage;
